// A stored WhatsApp template rendered as the shared WhatsAppCardPreview's props.
//
// Meta returns a template as a flat list of components (HEADER / BODY / FOOTER /
// BUTTONS) with numeric {{n}} placeholders. Every surface that shows a template
// the tenant has NOT yet filled in — the order notification rules, the ticket
// desk's support template — needs the same mapping, so it lives here once
// instead of being copied per screen.
//
// `placeholderByKey` is whatsappPlaceholderByKey(context): it turns each stored
// variable key into the named label the template was authored with, so the
// preview reads "Customer name" rather than "{{1}}".

const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

// The body with its numbered placeholders shown as the green .wa-fill pills the
// card preview styles (falls back to the raw {{n}} for a Meta-authored template
// that carries no keys of ours).
//
// `values` is the composer's per-variable input, index 0 = {{1}}: a slot the agent
// (or a linked record) has already filled shows that real text, an empty one shows
// its named pill. The settings screens pass none, so every slot is a pill there.
export const templateBodyHtml = (template, placeholderByKey, values = []) => {
    const keys = (template && template.variable_keys) || []

    return escapeHtml((template && template.body) || '')
        .replace(/\{\{\s*(\d+)\s*\}\}/g, (match, position) => {
            const index = Number(position) - 1
            const value = values[index]
            if (value && String(value).trim()) return escapeHtml(String(value).trim())

            const key = keys[index]
            const placeholder = key && placeholderByKey[key]
            return `<span class="wa-fill">${escapeHtml((placeholder && placeholder.label) || match)}</span>`
        })
        .replace(/\n/g, '<br>')
}

export const templateCardPreview = (template, placeholderByKey) => {
    const components = (template && template.components) || []
    const byType = (type) => components.find((component) => (component.type || '').toUpperCase() === type)
    const headerComponent = byType('HEADER')
    const footerComponent = byType('FOOTER')
    const buttonsComponent = byType('BUTTONS')
    const headerFormat = String((headerComponent && headerComponent.format) || (template && template.header_format) || 'TEXT').toUpperCase()
    const isImageHeader = headerFormat === 'IMAGE'

    return {
        headerImage: isImageHeader ? ((template && template.header_media_url) || '') : '',
        header: (!isImageHeader && headerComponent && headerComponent.text) || '',
        body: templateBodyHtml(template, placeholderByKey),
        footer: (footerComponent && footerComponent.text) || '',
        buttons: ((buttonsComponent && buttonsComponent.buttons) || []).map((button) => ({
            type: button.type,
            text: button.text,
            url: button.url
        }))
    }
}
